import {
  Alert,
  AlertIcon,
  Button,
  ButtonGroup,
  Checkbox,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useState } from 'react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { DialogBox } from '@duke-hq/ui/components/DialogBox/DialogBox';

interface TransferSupersededEnrolmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  currentCourseName: string | null;
  supersedingCourseName: string | null;
}

export function TransferSupersededEnrolmentModal({
  isOpen,
  onClose,
  taskId,
  currentCourseName,
  supersedingCourseName,
}: TransferSupersededEnrolmentModalProps) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();
  const [addSecondSemester, setAddSecondSemester] = useState(true);

  const transferMutation = trpcAdmin.support.task.transferSupersededEnrolment.useMutation({
    onSuccess: () => {
      toast({
        status: 'success',
        title: t`Enrolment transferred successfully`,
      });
      void utils.support.task.get.invalidate();
      void utils.support.task.list.invalidate(undefined, { refetchType: 'active' });
      onClose();
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: t`Failed to transfer enrolment`,
        description: error.message,
      });
    },
  });

  const handleTransfer = () => {
    transferMutation.mutate({ taskId, addSecondSemester });
  };

  return (
    <DialogBox
      isOpen={isOpen}
      onClose={onClose}
      header={t`Transfer to Superseding Course`}
      body={
        <VStack align="stretch" spacing={4}>
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">
              <Trans>
                This will withdraw the student from the current course
                {currentCourseName != null ? ` (${currentCourseName})` : ''} and create a new
                enrolment in the superseding course
                {supersedingCourseName != null ? ` (${supersedingCourseName})` : ''}. Completed
                units will be transferred as credit.
              </Trans>
            </Text>
          </Alert>

          <Checkbox
            isChecked={addSecondSemester}
            onChange={(e) => setAddSecondSemester(e.target.checked)}
          >
            <Text fontSize="sm">
              <Trans>Add second semester to the new enrolment</Trans>
            </Text>
          </Checkbox>
        </VStack>
      }
      footer={
        <ButtonGroup>
          <Button variant="secondary" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleTransfer} isLoading={transferMutation.isPending}>
            <Trans>Transfer Enrolment</Trans>
          </Button>
        </ButtonGroup>
      }
    />
  );
}
